import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { pricingAPI } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Skeleton } from '../../components/ui/skeleton';
import { Separator } from '../../components/ui/separator';
import { FileDown, Loader2, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { EN_CONTENT } from './pricingGuideContent';

const NAVY   = 'hsl(var(--c4p-navy-900))';
const ORANGE = 'hsl(var(--c4p-orange-600))';
const CURRENCIES = ['GBP', 'EUR', 'USD', 'CAD', 'AUD'];
const SYM = { GBP: '\u00a3', EUR: '\u20ac', USD: '$', CAD: 'CA$', AUD: 'A$' };
const fmt = (n) => Math.round(n || 0).toLocaleString('en-GB');

const TierChip = ({ tier }) => {
  const styles = [{ bg: '#eff6ff', color: '#1d4ed8' }, { bg: '#f0fdf4', color: '#15803d' }, { bg: '#fff7ed', color: '#c2410c' }];
  const s = styles[tier - 1] || styles[0];
  return <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={s}>Tier {tier}</span>;
};

const Section = ({ id, title, children }) => (
  <section id={id} className="space-y-4">
    <h2 className="text-lg font-black pb-2" style={{ fontFamily: 'var(--font-display)', color: NAVY, borderBottom: `2px solid ${NAVY}` }}>{title}</h2>
    {children}
  </section>
);

export default function PricingGuide() {
  const location  = useLocation();
  const isResourceView = location.pathname === '/resources/pricing-guide';
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [regions, setRegions]     = useState([]);
  const [selectedRegion, setSelectedRegion] = useState('eu-west-2');
  const [selectedCurrency, setSelectedCurrency] = useState('GBP');
  const c = EN_CONTENT;

  useEffect(() => {
    const token = localStorage.getItem('c4p_access_token');
    const h = { Authorization: `Bearer ${token}` };
    Promise.all([
      pricingAPI.getConfig(),
      fetch('/api/admin/server-catalog?server_type=HANADB&include_inactive=true', { headers: h }).then(r => r.json()),
      fetch('/api/admin/server-catalog?server_type=SQLDB&include_inactive=true',  { headers: h }).then(r => r.json()),
      fetch('/api/admin/server-catalog?server_type=Application&include_inactive=true', { headers: h }).then(r => r.json()),
      fetch('/api/admin/extras', { headers: h }).then(r => r.json()).catch(() => []),
      fetch('/api/admin/regions', { headers: h }).then(r => r.json()).catch(() => []),
    ]).then(([cfgRes, hana, sql, apps, extras, regs]) => {
      setData({
        config: cfgRes.data,
        hana: Array.isArray(hana) ? hana : [],
        sql:  Array.isArray(sql)  ? sql  : [],
        apps: Array.isArray(apps) ? apps.filter(s => !s.code?.includes('Linux') && !s.code?.includes('Ubuntu') && !s.code?.includes('GPU') && s.is_active !== false && !s.is_deleted) : [],
        extras: Array.isArray(extras) ? extras : [],
      });
      if (Array.isArray(regs) && regs.length) {
        setRegions(regs);
        setSelectedRegion(regs[0]?.aws_region_code || 'eu-west-2');
        setSelectedCurrency(regs[0]?.default_currency || 'GBP');
      }
    }).catch(() => toast.error('Failed to load pricing data'))
      .finally(() => setLoading(false));
  }, []);

  const handleRegionChange = (code) => {
    setSelectedRegion(code);
    const reg = regions.find(r => r.aws_region_code === code);
    if (reg) setSelectedCurrency(reg.default_currency);
  };

  const downloadPDF = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem('c4p_access_token');
      const res = await fetch(`/api/admin/pricing-guide/pdf?region=${selectedRegion}&currency=${selectedCurrency}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const regionName = regions.find(r => r.aws_region_code === selectedRegion)?.name?.replace(/[^a-zA-Z0-9]/g, '-') || selectedRegion;
      const a = document.createElement('a'); a.href = url; a.download = `C4P-Pricing-Guide-${regionName}-${selectedCurrency}.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
      toast.success('Pricing Guide downloaded');
    } catch { toast.error('Download failed'); }
    finally { setDownloading(false); }
  };

  if (loading) return <div className="space-y-4">{[1,2,3,4].map(i => <Skeleton key={i} className="h-32 w-full" />)}</div>;
  if (!data) return null;

  const { config, hana, sql, apps, extras } = data;
  const tiers = config?.advanced_tier_breaks || [];
  const stdPrices = config?.standard_pupm?.prices_by_currency || {};
  const stdPUPM = stdPrices[selectedCurrency] || 0;

  const hanaBase = hana.find(s => s.is_base_for_hana);
  const hanaUpgrades = hana.filter(s => !s.is_base_for_hana && s.is_active !== false && !s.custom_only);
  const sqlBase = sql.find(s => s.is_base_for_sql);
  const sqlUpgrades = sql.filter(s => !s.is_base_for_sql && s.is_active !== false && !s.custom_only);
  const customHanaServers = hana.filter(s => s.custom_only && s.is_active !== false);

  // Determine DB pricing mode - if ALL upgrades across both HANA and SQL are static, use static callout
  const allDbUpgrades = [...hanaUpgrades, ...sqlUpgrades];
  const allDbStatic = allDbUpgrades.length > 0 && allDbUpgrades.every(s => s.pricing_mode === 'static');
  const dbCallout = allDbStatic ? c.s4CalloutStatic : c.s4Callout;

  const appServers = apps
    .filter(s => (s.region_price_matrix || {})[selectedRegion]?.[selectedCurrency] > 0)
    .sort((a, b) => (a.region_price_matrix?.[selectedRegion]?.[selectedCurrency] || 0) - (b.region_price_matrix?.[selectedRegion]?.[selectedCurrency] || 0));

  const selectedRegionName = regions.find(r => r.aws_region_code === selectedRegion)?.name || selectedRegion;
  const sym = SYM[selectedCurrency] || selectedCurrency;

  const DbTable = ({ servers, baseKey, title }) => {
    const base = servers.find(s => s[baseKey]);
    const upgrades = servers.filter(s => !s[baseKey] && s.is_active !== false);
    // If ALL upgrades are in static mode, collapse to a single price column
    const allStatic = upgrades.length > 0 && upgrades.every(s => s.pricing_mode === 'static');
    const getUpgradePrice = (s, tier) =>
      s.pricing_mode === 'static'
        ? (s.prices_by_currency?.[selectedCurrency] || 0)
        : (s.price_matrix?.[tier]?.[selectedCurrency] || 0);
    return (
      <div className="rounded-xl border bg-white overflow-hidden">
        <div className="px-4 py-2.5" style={{ background: 'hsl(var(--muted))', borderBottom: '1px solid hsl(var(--border))' }}>
          <p className="text-xs font-bold uppercase tracking-wide" style={{ color: NAVY }}>{title} - {selectedRegionName} · {selectedCurrency}</p>
        </div>
        <div className="overflow-x-auto">
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          <thead><tr style={{ background: NAVY, color: 'white' }}>
            <th className="text-left px-4 py-2 text-xs font-bold" style={{ width: '28%' }}>Server</th>
            <th className="text-center px-3 py-2 text-xs font-bold">CPU / RAM</th>
            <th className="text-center px-3 py-2 text-xs font-bold">Data + Backup</th>
            {allStatic
              ? <th className="text-right px-3 py-2 text-xs font-bold">Monthly Price</th>
              : <>
                  <th className="text-right px-3 py-2 text-xs font-bold"><TierChip tier={1} /></th>
                  <th className="text-right px-3 py-2 text-xs font-bold"><TierChip tier={2} /></th>
                  <th className="text-right px-3 py-2 text-xs font-bold"><TierChip tier={3} /></th>
                </>
            }
          </tr></thead>
          <tbody>
            {base && (
              <tr style={{ background: '#f0fdf4', borderBottom: '1px solid hsl(var(--border))' }}>
                <td className="px-4 py-2.5">
                  <p className="font-bold text-sm" style={{ color: NAVY }}>{base.memory_gb}GB {title.includes('HANA') ? 'HANA' : 'SQL'}</p>
                  <p className="text-xs" style={{ color: '#15803d' }}>&#10003; {title.includes('HANA') ? c.hanaIncludedNote : c.sqlIncludedNote}</p>
                </td>
                <td className="text-center px-3 py-2.5 text-sm">{base.cpu} vCPU / {base.memory_gb}GB</td>
                <td className="text-center px-3 py-2.5 text-sm">{base.included_data_gb}GB + {base.included_backup_gb}GB</td>
                {allStatic
                  ? <td className="text-right font-bold px-3 py-2.5 text-sm" style={{ color: '#15803d' }}>+{sym}0</td>
                  : ['1','2','3'].map(t => <td key={t} className="text-right font-bold px-3 py-2.5 text-sm" style={{ color: '#15803d' }}>+{sym}0</td>)
                }
              </tr>
            )}
            {upgrades.map((s, i) => (
              <tr key={i} style={{ background: i % 2 === 0 ? 'white' : '#f8fafc', borderBottom: '1px solid hsl(var(--border))' }}>
                <td className="px-4 py-2.5">
                  <p className="font-bold text-sm" style={{ color: NAVY }}>{s.memory_gb}GB {title.includes('HANA') ? 'HANA' : 'SQL'}</p>
                  <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>{title.includes('HANA') ? c.hanaUpgradeNote : c.sqlUpgradeNote}</p>
                </td>
                <td className="text-center px-3 py-2.5 text-sm">{s.cpu} vCPU / {s.memory_gb}GB</td>
                <td className="text-center px-3 py-2.5 text-sm">{s.included_data_gb}GB + {s.included_backup_gb}GB</td>
                {allStatic
                  ? <td className="text-right tabular-nums px-3 py-2.5 text-sm font-bold" style={{ color: NAVY }}>{sym}{fmt(getUpgradePrice(s, '1'))}</td>
                  : ['1','2','3'].map(t => (
                      <td key={t} className="text-right tabular-nums px-3 py-2.5 text-sm font-bold" style={{ color: NAVY }}>
                        {sym}{fmt(getUpgradePrice(s, t))}
                      </td>
                    ))
                }
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        <div className="px-4 py-2 text-xs flex gap-4 flex-wrap" style={{ borderTop: '1px solid hsl(var(--border))', color: 'hsl(var(--muted-fg))' }}>
          {allStatic
            ? <span>Fixed price - applies at all user counts</span>
            : <>
                <span><TierChip tier={1} /> {tiers[0]?.min_users}–{tiers[0]?.max_users} users</span>
                <span><TierChip tier={2} /> {tiers[1]?.min_users}–{tiers[1]?.max_users} users</span>
                <span><TierChip tier={3} /> {tiers[2]?.min_users}+ users</span>
              </>
          }
          {!title.includes('HANA') && <span style={{ marginLeft: 'auto', color: '#c2410c', fontWeight: 600 }}>&#9888; {c.byolSql}</span>}
          {title.includes('HANA') && <span style={{ marginLeft: 'auto', color: '#0369a1', fontWeight: 600 }}>&#9432; {c.byolHana}</span>}
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6 pb-10" data-testid="pricing-guide">

      {/* Standard resource navigation - breadcrumb left, Download PDF right */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <Link to="/resources" className="inline-flex items-center gap-1.5 text-sm font-medium"
            style={{ color: 'hsl(var(--muted-fg))' }}>
            <ArrowLeft size={14} /> Resources
          </Link>
          <span style={{ color: 'hsl(var(--muted-fg))' }}>/</span>
          <span className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>Sales Enablement</span>
        </div>
        <Button onClick={downloadPDF} disabled={downloading}
          style={{ background: NAVY, color: 'white' }} className="gap-2">
          {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown size={14} />}
          {c.downloadBtn} - {selectedRegionName} · {selectedCurrency}
        </Button>
      </div>

      {/* Header card - same style as ResourceDetailPage */}
      <div className="rounded-2xl overflow-hidden shadow-sm" style={{ border: '1px solid #e2e8f0' }}>
        <div className="px-8 py-6" style={{ background: `linear-gradient(135deg, ${NAVY} 0%, #2d5a8e 100%)` }}>
          <span className="text-xs font-bold px-2.5 py-1 rounded-full mb-3 inline-block"
            style={{ background: '#16a34a', color: 'white' }}>
            Sales Enablement
          </span>
          <h1 className="text-2xl font-black text-white mt-2 mb-2" style={{ fontFamily: 'var(--font-display)' }}>
            {c.pageTitle}
          </h1>
          <p className="text-sm" style={{ color: 'rgba(255,255,255,0.75)' }}>{c.pageSubtitle}</p>
        </div>
      </div>

      {/* Region / Currency filter bar */}
      <div className="rounded-xl border bg-white px-5 py-4 flex items-center gap-3 flex-wrap">
        <p className="text-sm font-semibold flex-shrink-0" style={{ color: NAVY }}>Filter pricing:</p>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="space-y-0.5">
            <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'hsl(var(--muted-fg))' }}>{c.regionLabel}</p>
            <Select value={selectedRegion} onValueChange={handleRegionChange}>
              <SelectTrigger className="w-44 text-sm h-9" data-testid="guide-region-select"><SelectValue /></SelectTrigger>
              <SelectContent>{regions.map(r => <SelectItem key={r.aws_region_code} value={r.aws_region_code}>{r.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-0.5">
            <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'hsl(var(--muted-fg))' }}>{c.currencyLabel}</p>
            <Select value={selectedCurrency} onValueChange={setSelectedCurrency}>
              <SelectTrigger className="w-32 text-sm h-9" data-testid="guide-currency-select"><SelectValue /></SelectTrigger>
              <SelectContent>{CURRENCIES.map(cur => <SelectItem key={cur} value={cur}>{SYM[cur]} {cur}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Section 1 - 2×2 grid of 4 product cards */}
      <Section id="overview" title={c.s1Title}>
        {(() => {
          const PRODUCTS = [
            {
              name: 'C4P Standard', nameColor: NAVY,
              tag: c.stdTag, tagStyle: { background: 'hsl(var(--muted))', color: NAVY },
              accent: NAVY, bg: 'white', border: 'hsl(var(--border))',
              intro: c.stdIntro,
              items: [[true,c.stdF1],[true,c.stdF2],[true,c.stdF3],[false,c.stdF4],['opt',c.stdF5],['opt',c.stdF6]],
            },
            {
              name: 'C4P Advanced', nameColor: NAVY,
              tag: c.advTag, tagStyle: { background: NAVY, color: 'white' },
              accent: '#39b8e2', bg: '#f0f9ff', border: 'hsl(196 75% 56% / 0.35)',
              intro: c.advIntro,
              items: [[true,c.advF1],[true,c.advF2],[true,c.advF3],[true,c.advF4],[true,c.advF5]],
            },
            {
              name: 'C4P MTPro', nameColor: 'hsl(265 89% 45%)',
              tag: c.mtpTag, tagStyle: { background: 'hsl(265 89% 45%)', color: 'white' },
              accent: 'hsl(265 89% 45%)', bg: 'hsl(265 89% 97%)', border: 'hsl(265 89% 68% / 0.35)',
              intro: c.mtpIntro,
              items: [[true,c.mtpF1],[true,c.mtpF2],[true,c.mtpF3],[true,c.mtpF4],[true,c.mtpF5]],
            },
            {
              name: 'C4P Custom', nameColor: '#0e7490',
              tag: c.cusTag, tagStyle: { background: '#0e7490', color: 'white' },
              accent: '#0e7490', bg: '#f0fdfa', border: '#99f6e4',
              intro: c.cusIntro,
              items: [['opt',c.cusF1],['opt',c.cusF2],['opt',c.cusF3],[true,c.cusF4],['opt',c.cusF5]],
            },
          ];
          return (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              {PRODUCTS.map(p => (
                <div key={p.name} style={{ borderRadius: '12px', overflow: 'hidden', border: `1px solid ${p.border}` }}>
                  <div className="px-4 pt-4 pb-3" style={{ background: p.bg, borderTop: `4px solid ${p.accent}` }}>
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <p className="font-black text-sm" style={{ fontFamily: 'var(--font-display)', color: p.nameColor }}>{p.name}</p>
                      <span className="text-xs font-bold px-2 py-0.5 rounded-full whitespace-nowrap" style={p.tagStyle}>{p.tag}</span>
                    </div>
                    <p className="text-xs leading-relaxed mt-2" style={{ color: 'hsl(var(--muted-fg))' }}>{p.intro}</p>
                  </div>
                  <div className="px-4 pt-3 pb-4 space-y-1.5" style={{ background: p.bg, borderTop: `1px solid ${p.border}` }}>
                    {p.items.map(([inc, label], i) => (
                      <div key={i} className="flex items-center gap-2 text-xs">
                        <span style={{
                          color: inc === true ? '#15803d' : inc === 'opt' ? p.accent : '#d1d5db',
                          flexShrink: 0, fontWeight: 700,
                        }}>{inc === true ? '\u2713' : inc === 'opt' ? '+' : '\u00d7'}</span>
                        <span style={{ color: inc === true ? '#1e293b' : inc === 'opt' ? '#374151' : '#94a3b8' }}>{label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          );
        })()}
      </Section>

      {/* Section 2: Platform Bundle */}
      <Section id="bundle" title={c.s2Title}>
        {/* Headline stats */}
        <div className="grid grid-cols-4 gap-3">
          {[['99.995%',c.stat1Lbl],[c.stat2Val,c.stat2Lbl],['9',c.stat3Lbl],['15+',c.stat4Lbl]].map(([val, label]) => (
            <div key={label} className="rounded-xl p-3 text-center" style={{ background: NAVY }}>
              <p className="text-xl font-black text-white">{val}</p>
              <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.6)' }}>{label}</p>
            </div>
          ))}
        </div>
        <p className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>{c.s2Intro}</p>
        {/* Feature grid */}
        <div className="grid grid-cols-3 gap-3">
          {[1,2,3,4,5,6,7,8,9,10,11,12,13,14,15].map(i => (
            <div key={i} className="rounded-xl border bg-white p-3">
              <div className="flex items-start gap-2">
                <span className="font-black flex-shrink-0 mt-0.5" style={{ color: '#15803d', fontSize: '11px' }}>{'\u2713'}</span>
                <div>
                  <p className="text-xs font-bold" style={{ color: NAVY }}>{c[`feat${i}Title`]}</p>
                  <p className="text-xs mt-0.5 leading-relaxed" style={{ color: 'hsl(var(--muted-fg))' }}>{c[`feat${i}Desc`]}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
        {/* White-labelling differentiator */}
        <div className="rounded-xl p-3 flex gap-3 items-start" style={{ background: '#f0f9ff', border: '1px solid hsl(196 75% 56% / 0.35)' }}>
          <span className="font-black flex-shrink-0 mt-0.5" style={{ color: '#39b8e2', fontSize: '13px' }}>+</span>
          <div>
            <p className="text-xs font-bold" style={{ color: NAVY }}>{c.wlTitle}</p>
            <p className="text-xs mt-0.5 leading-relaxed" style={{ color: 'hsl(var(--muted-fg))' }}>{c.wlDesc}</p>
          </div>
        </div>
      </Section>

      {/* Section 3: PUPM */}
      <Section id="pupm" title={c.s3Title}>
        <p className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>All pricing shown below is for the <strong>{selectedRegionName} region</strong> in <strong>{selectedCurrency}</strong>. All costs are stated as monthly recurring charges.</p>
        <div className="grid grid-cols-2 gap-4">
          <div className="rounded-xl border bg-white p-4">
            <p className="text-xs font-bold uppercase tracking-wide mb-3" style={{ color: NAVY }}>C4P Standard - Flat Rate</p>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-black tabular-nums" style={{ color: NAVY }}>{sym}{fmt(stdPUPM)}</span>
              <span className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>per user / month</span>
            </div>
            <p className="text-xs mt-2" style={{ color: 'hsl(var(--muted-fg))' }}>Same rate regardless of user count. No minimum.</p>
            <p className="text-xs mt-1 font-semibold" style={{ color: NAVY }}>e.g. 12 users = {sym}{fmt(12 * stdPUPM)}/mo</p>
          </div>
          <div className="rounded-xl border bg-white p-4 space-y-3">
            <div>
              <p className="text-xs font-bold uppercase tracking-wide mb-1" style={{ color: NAVY }}>C4P Advanced &amp; MTPro - Volume Pricing (min. 20 users)</p>
              {tiers.length >= 2 && (
                <p className="text-sm font-bold" style={{ color: NAVY }}>
                  From {sym}{fmt(tiers[0]?.prices_by_currency?.[selectedCurrency])} down to as low as{' '}
                  {sym}{fmt(tiers[tiers.length - 1]?.prices_by_currency?.[selectedCurrency])}/user/month
                </p>
              )}
              <p className="text-xs mt-1" style={{ color: 'hsl(var(--muted-fg))' }}>
                Volume discounts apply automatically - the more users, the lower the per-user rate.
              </p>
            </div>
            {/* Full tier band table */}
            <table className="w-full" style={{ borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead>
                <tr style={{ background: NAVY, color: 'white' }}>
                  <th className="text-left px-3 py-1.5 font-semibold rounded-tl-lg">User Count</th>
                  <th className="text-right px-3 py-1.5 font-semibold rounded-tr-lg">{selectedCurrency} / user / mo</th>
                </tr>
              </thead>
              <tbody>
                {[...tiers].sort((a, b) => a.min_users - b.min_users).map((tier, i, arr) => {
                  const pupm  = tier.prices_by_currency?.[selectedCurrency] || 0;
                  const isLast = i === arr.length - 1;
                  return (
                    <tr key={tier.id} style={{ background: isLast ? '#f0fdf4' : i % 2 === 0 ? 'white' : '#f8fafc', borderBottom: '1px solid #f1f5f9' }}>
                      <td className="px-3 py-2 font-semibold" style={{ color: NAVY }}>
                        {tier.min_users}{tier.max_users ? `–${tier.max_users}` : '+'} users
                        {i === 0 && <span className="ml-1.5 text-xs font-normal" style={{ color: 'hsl(var(--muted-fg))' }}>- minimum</span>}
                        {isLast && <span className="ml-1.5 text-xs font-semibold" style={{ color: '#16a34a' }}>- best rate</span>}
                      </td>
                      <td className="px-3 py-2 text-right font-bold tabular-nums" style={{ color: NAVY }}>{sym}{fmt(pupm)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>
              Discounts apply automatically - enter your exact user count in the quote tool for your precise price.
            </p>
          </div>
        </div>
      </Section>

      {/* Section 3: DB Servers */}
      <Section id="db" title={c.s4Title}>
        <div className="rounded-xl border p-3" style={{ background: 'hsl(var(--accent))', borderColor: 'hsl(var(--c4p-brand-blue) / 0.3)' }}>
          <p className="text-sm">{dbCallout}</p>
        </div>
        <div className="rounded-lg p-3 text-sm" style={{ background: '#f0fdfa', border: '1px solid #99f6e4', color: '#0e7490' }}>
          <strong>C4P Custom builds</strong> have their own per-build server pricing — any HANA or SQL configuration can be specified, including options not in the tables below. See Section 7.
        </div>
        <DbTable servers={[hanaBase, ...hanaUpgrades].filter(Boolean)} baseKey="is_base_for_hana" title={c.hanaSubtitle} />
        <DbTable servers={[sqlBase, ...sqlUpgrades].filter(Boolean)} baseKey="is_base_for_sql" title={c.sqlSubtitle} />

        {/* Database Allowance */}
        <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'hsl(196 75% 56% / 0.4)' }}>
          <div className="px-4 py-3" style={{ background: NAVY }}>
            <p className="text-sm font-black text-white" style={{ fontFamily: 'var(--font-display)' }}>{c.dbAllowTitle}</p>
            <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.65)' }}>{c.dbAllowSubtitle}</p>
          </div>
          <div className="grid grid-cols-2" style={{ borderTop: '1px solid hsl(196 75% 56% / 0.3)' }}>
            <div className="p-4" style={{ borderRight: '1px solid hsl(var(--border))', background: 'white' }}>
              <p className="text-xs font-bold uppercase tracking-wide mb-2" style={{ color: NAVY }}>{c.dbStdColTitle}</p>
              <p className="text-xs mb-3" style={{ color: 'hsl(var(--muted-fg))' }}>{c.dbStdColIntro}</p>
              <div className="space-y-2">
                <div className="rounded-lg p-2.5" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                  <p className="text-xs font-bold" style={{ color: '#15803d' }}>{c.dbHanaAllowTitle}</p>
                  <p className="text-xs mt-0.5" style={{ color: '#166534' }}>{c.dbHanaAllowDesc}</p>
                </div>
                <div className="rounded-lg p-2.5" style={{ background: '#eff6ff', border: '1px solid #bfdbfe' }}>
                  <p className="text-xs font-bold" style={{ color: '#1d4ed8' }}>{c.dbSqlAllowTitle}</p>
                  <p className="text-xs mt-0.5" style={{ color: '#1e40af' }}>{c.dbSqlAllowDesc}</p>
                  {(() => {
                    const dbEx = extras.find(e => e.code === 'C4P-DB-SUB');
                    const dbPrice = dbEx?.prices_by_currency?.[selectedCurrency] || 0;
                    if (!dbPrice) return null;
                    return (
                      <p className="text-xs font-bold mt-2 pt-1.5" style={{ color: NAVY, borderTop: '1px solid #bfdbfe' }}>
                        {SYM[selectedCurrency] || ''}{dbPrice} / database / month
                      </p>
                    );
                  })()}
                </div>
              </div>
            </div>
            <div className="p-4" style={{ background: '#f0f9ff' }}>
              <p className="text-xs font-bold uppercase tracking-wide mb-2" style={{ color: NAVY }}>{c.dbAdvColTitle}</p>
              <p className="text-xs mb-3" style={{ color: 'hsl(var(--muted-fg))' }}>{c.dbAdvColIntro}</p>
              <div className="space-y-2">
                <div className="rounded-lg p-2.5" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                  <p className="text-xs font-bold" style={{ color: '#15803d' }}>{c.dbHanaAdvTitle}</p>
                  <p className="text-xs mt-0.5" style={{ color: '#166534' }}>{c.dbHanaAdvDesc}</p>
                  <div className="mt-2 space-y-0.5">
                    {hana.filter(s => s.is_active !== false && !s.custom_only).map((s, i) => (
                      <div key={i} className="flex justify-between text-xs tabular-nums">
                        <span style={{ color: '#374151' }}>{s.memory_gb} GB server</span>
                        <span className="font-bold" style={{ color: '#15803d' }}>&rarr; {Math.max(0, s.memory_gb - 40)} {c.dbHanaAdvAvail}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="rounded-lg p-2.5" style={{ background: '#eff6ff', border: '1px solid #bfdbfe' }}>
                  <p className="text-xs font-bold" style={{ color: '#1d4ed8' }}>{c.dbSqlAdvTitle}</p>
                  <p className="text-xs mt-0.5" style={{ color: '#1e40af' }}>{c.dbSqlAdvDesc}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* Section 4: App Servers */}
      <Section id="appservers" title={c.s5Title}>
        <div className="grid grid-cols-2 gap-4">
          <div className="rounded-xl border p-4">
            <p className="text-sm font-bold mb-1" style={{ color: NAVY }}>{c.s5InclTitle}</p>
            <p className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>{c.s5InclDesc}</p>
          </div>
          <div className="rounded-xl border p-4" style={{ background: '#fff7ed', borderColor: '#fed7aa' }}>
            <p className="text-sm font-bold mb-1" style={{ color: '#c2410c' }}>{c.s5StdTitle}</p>
            <p className="text-sm" style={{ color: '#9a3412' }}>{c.s5StdDesc}</p>
          </div>
        </div>
        <div className="rounded-xl border bg-white overflow-hidden">
          <div className="px-4 py-2.5" style={{ background: 'hsl(var(--muted))', borderBottom: '1px solid hsl(var(--border))' }}>
            <p className="text-xs font-bold uppercase tracking-wide" style={{ color: NAVY }}>Windows App Servers - {selectedRegionName} · {selectedCurrency}</p>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead><tr style={{ background: NAVY, color: 'white' }}>
              <th className="text-left px-4 py-2 text-xs font-bold" style={{ width: '35%' }}>Specification</th>
              <th className="text-center px-3 py-2 text-xs font-bold">CPU / RAM</th>
              <th className="text-center px-3 py-2 text-xs font-bold">Root Vol.</th>
              <th className="text-right px-3 py-2 text-xs font-bold">Monthly ({selectedCurrency})</th>
            </tr></thead>
            <tbody>
              {appServers.map((s, i) => (
                <tr key={i} style={{ background: i%2===0?'white':'#f8fafc', borderBottom: '1px solid hsl(var(--border))' }}>
                  <td className="px-4 py-2.5">
                    <p className="font-semibold text-sm">{s.description}</p>
                    {s.is_included_app_server && <p className="text-xs" style={{ color: '#15803d' }}>&#10003; {c.s5InclTag}</p>}
                  </td>
                  <td className="text-center px-3 py-2.5 text-sm">{s.cpu} vCPU / {s.memory_gb}GB</td>
                  <td className="text-center px-3 py-2.5 text-sm">{s.included_root_gb}GB SSD</td>
                  <td className="text-right tabular-nums px-3 py-2.5 text-sm font-bold" style={{ color: NAVY }}>
                    {sym}{fmt(s.region_price_matrix?.[selectedRegion]?.[selectedCurrency])}
                  </td>
                </tr>
              ))}
              {appServers.length === 0 && (
                <tr><td colSpan={4} className="text-center py-6 text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>No priced App Servers for {selectedRegionName} · {selectedCurrency}</td></tr>
              )}
            </tbody>
          </table>
          <div className="px-4 py-2 text-xs" style={{ color: 'hsl(var(--muted-fg))', borderTop: '1px solid hsl(var(--border))' }}>Prices are fixed for this region - no FX conversion. Linux, Ubuntu Pro and GPU variants available at the same regional pricing.</div>
        </div>
      </Section>

      {/* Section 5: Add-ons */}
      <Section id="addons" title={c.s6Title}>
        <div className="grid grid-cols-2 gap-4">
          {[[c.vpnTitle,c.vpnDesc],[c.mfaTitle,c.mfaDesc],[c.wcTitle,c.wcDesc],[c.cccTitle,c.cccDesc]].map(([t,b],i) => (
            <div key={i} className="rounded-xl border p-4"><p className="text-sm font-bold mb-1" style={{ color: NAVY }}>{t}</p><p className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>{b}</p></div>
          ))}
          {/* Additional Database — dynamically from extras catalog */}
          {(() => {
            const dbEx = extras.find(e => e.code === 'C4P-DB-SUB');
            if (!dbEx) return null;
            const dbPrice = dbEx.prices_by_currency?.[selectedCurrency] || 0;
            return (
              <div className="rounded-xl border p-4" style={{ background: '#f0fdf4', borderColor: '#bbf7d0', gridColumn: 'span 2' }}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-bold mb-1" style={{ color: '#15803d' }}>{c.addDbTitle}</p>
                    <p className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>{c.addDbDesc}</p>
                  </div>
                  {dbPrice > 0 && (
                    <div className="text-right flex-shrink-0">
                      <p className="text-base font-black tabular-nums" style={{ color: NAVY }}>{SYM[selectedCurrency]}{dbPrice}</p>
                      <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>/ database / month</p>
                      <p className="text-xs mt-0.5 font-medium" style={{ color: '#15803d' }}>2 databases included free</p>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}
        </div>

        {/* Web Client & Mobile User pricing */}
        {(config?.web_client_pupm || config?.mobile_user_pupm) && (() => {
          const wcPrices  = config?.web_client_pupm?.prices_by_currency  || {};
          const mobPrices = config?.mobile_user_pupm?.prices_by_currency || {};
          const wcPrice  = wcPrices[selectedCurrency]  || 0;
          const mobPrice = mobPrices[selectedCurrency] || 0;
          if (!wcPrice && !mobPrice) return null;
          return (
            <div className="rounded-xl border bg-white overflow-hidden">
              <div className="px-4 py-2.5" style={{ background: 'hsl(var(--muted))', borderBottom: '1px solid hsl(var(--border))' }}>
                <p className="text-xs font-bold uppercase tracking-wide" style={{ color: NAVY }}>
                  Add-on User Pricing - {selectedCurrency} · per user / month
                </p>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead><tr style={{ background: NAVY, color: 'white' }}>
                  <th className="text-left px-4 py-2 text-xs font-bold" style={{ width: '40%' }}>{c.colUserType}</th>
                  <th className="text-left px-4 py-2 text-xs font-bold">{c.colDescription}</th>
                  <th className="text-right px-4 py-2 text-xs font-bold">{selectedCurrency} / user / month</th>
                </tr></thead>
                <tbody>
                  {wcPrice > 0 && (
                    <tr style={{ borderBottom: '1px solid hsl(var(--border))' }}>
                      <td className="px-4 py-2.5 font-semibold text-sm">{c.wcUserLabel}</td>
                      <td className="px-4 py-2.5 text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>{c.wcUserDesc}</td>
                      <td className="text-right tabular-nums px-4 py-2.5 font-bold text-sm" style={{ color: NAVY }}>{sym}{fmt(wcPrice)}</td>
                    </tr>
                  )}
                  {mobPrice > 0 && (
                    <tr>
                      <td className="px-4 py-2.5 font-semibold text-sm">{c.mobUserLabel}</td>
                      <td className="px-4 py-2.5 text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>{c.mobUserDesc}</td>
                      <td className="text-right tabular-nums px-4 py-2.5 font-bold text-sm" style={{ color: NAVY }}>{sym}{fmt(mobPrice)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
              <div className="px-4 py-2 text-xs" style={{ color: 'hsl(var(--muted-fg))', borderTop: '1px solid hsl(var(--border))' }}>
                {c.addonsFootnote}
              </div>
            </div>
          );
        })()}
      </Section>

      {/* Section 7: C4P Custom */}
      <Section id="custom" title={c.s7CustomTitle}>
        <p className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>{c.s7CustomIntro}</p>
        <div className="grid grid-cols-2 gap-4">
          <div className="rounded-xl border p-4 space-y-2.5" style={{ background: '#f0fdfa', borderColor: '#99f6e4' }}>
            <p className="text-sm font-bold" style={{ color: '#0e7490' }}>{c.cusWhenTitle}</p>
            {[c.cusWhen1, c.cusWhen2, c.cusWhen3, c.cusWhen4].map((t, i) => (
              <div key={i} className="flex items-start gap-2 text-xs">
                <span className="font-bold flex-shrink-0 mt-0.5" style={{ color: '#0e7490' }}>&#10003;</span>
                <span style={{ color: '#334155' }}>{t}</span>
              </div>
            ))}
          </div>
          <div className="rounded-xl border p-4 space-y-2.5">
            <p className="text-sm font-bold" style={{ color: NAVY }}>{c.cusPricingTitle}</p>
            {[c.cusPricing1, c.cusPricing2, c.cusPricing3, c.cusPricing4].map((t, i) => (
              <div key={i} className="flex items-start gap-2 text-xs">
                <span className="font-bold flex-shrink-0 mt-0.5" style={{ color: ORANGE }}>+</span>
                <span style={{ color: '#334155' }}>{t}</span>
              </div>
            ))}
          </div>
        </div>

        {customHanaServers.length > 0 && (
          <>
            <p className="text-sm font-bold" style={{ color: NAVY }}>{c.cusDbTitle}</p>
            <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>{c.cusDbIntro}</p>
            <div className="rounded-xl border bg-white overflow-hidden">
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead><tr style={{ background: '#0e7490', color: 'white' }}>
                  <th className="text-left px-4 py-2 text-xs font-bold" style={{ width: '40%' }}>Server</th>
                  <th className="text-center px-3 py-2 text-xs font-bold">CPU / RAM</th>
                  <th className="text-left px-3 py-2 text-xs font-bold">Notes</th>
                </tr></thead>
                <tbody>
                  {customHanaServers.map((s, i) => (
                    <tr key={i} style={{ background: i % 2 === 0 ? 'white' : '#f8fafc', borderBottom: '1px solid hsl(var(--border))' }}>
                      <td className="px-4 py-2.5">
                        <p className="font-bold text-sm" style={{ color: '#0e7490' }}>{s.description}</p>
                        <p className="text-xs font-mono" style={{ color: 'hsl(var(--muted-fg))' }}>{s.code}</p>
                      </td>
                      <td className="text-center px-3 py-2.5 text-sm">{s.cpu} vCPU / {s.memory_gb}GB</td>
                      <td className="px-3 py-2.5 text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>
                        Private dedicated HANA environment for smaller deployments requiring full isolation.
                        {(() => {
                          const p = s.standalone_region_price_matrix?.[selectedRegion]?.[selectedCurrency]
                            || s.standalone_prices_by_currency?.[selectedCurrency] || 0;
                          return p > 0 ? <> <strong style={{ color: NAVY }}>{sym}{fmt(p)}/mo</strong></> : <> {c.cusContactPsm}.</>;
                        })()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* Worked example */}
        {(() => {
          const standaloneConfig = config?.standalone_pupm;
          const standalonePrice = standaloneConfig?.prices_by_currency?.[selectedCurrency] || 0;
          const customHana = customHanaServers[0];
          const hanaPrice = customHana
            ? (customHana.standalone_region_price_matrix?.[selectedRegion]?.[selectedCurrency]
               || customHana.standalone_prices_by_currency?.[selectedCurrency] || 0)
            : 0;
          const smallApp = appServers[0];
          const appPrice = smallApp?.region_price_matrix?.[selectedRegion]?.[selectedCurrency] || 0;
          const vpcExtra = extras.find(e => e.name?.toUpperCase().includes('VPC'));
          const vpcPrice = vpcExtra?.prices_by_currency?.[selectedCurrency] || 0;
          const allPriced = standalonePrice > 0 && hanaPrice > 0 && appPrice > 0;
          const total = 5 * standalonePrice + hanaPrice + appPrice + vpcPrice;

          return (
            <div className="rounded-xl border bg-white overflow-hidden" style={{ borderColor: '#99f6e4' }}>
              <div className="px-4 py-2.5 flex items-center justify-between" style={{ background: '#f0fdfa', borderBottom: '1px solid #99f6e4' }}>
                <div>
                  <p className="text-sm font-black" style={{ color: '#0e7490' }}>{c.cusExampleTitle}</p>
                  <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>{c.cusExampleLabel} &middot; {selectedCurrency} &middot; {selectedRegionName}</p>
                </div>
                <span className="text-xs font-bold px-2.5 py-1 rounded-full" style={{ background: '#0e7490', color: 'white' }}>Custom</span>
              </div>
              <div className="px-4 py-3 space-y-1.5">
                <div className="flex items-center justify-between text-sm">
                  <span style={{ color: 'hsl(var(--muted-fg))' }}>5 users &times; {standalonePrice > 0 ? `${sym}${fmt(standalonePrice)}/user (Custom PUPM)` : 'Custom PUPM'}</span>
                  {standalonePrice > 0 ? <span className="font-bold tabular-nums" style={{ color: NAVY }}>{sym}{fmt(5 * standalonePrice)}</span> : <span className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>{c.cusContactPsm}</span>}
                </div>
                {customHana && (
                  <div className="flex items-center justify-between text-sm">
                    <span style={{ color: 'hsl(var(--muted-fg))' }}>{customHana.description} (4vCPU/64GB)</span>
                    {hanaPrice > 0 ? <span className="font-bold tabular-nums" style={{ color: NAVY }}>{sym}{fmt(hanaPrice)}</span> : <span className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>{c.cusContactPsm}</span>}
                  </div>
                )}
                {smallApp && appPrice > 0 && (
                  <div className="flex items-center justify-between text-sm">
                    <span style={{ color: 'hsl(var(--muted-fg))' }}>{smallApp.description} (smallest available)</span>
                    <span className="font-bold tabular-nums" style={{ color: NAVY }}>{sym}{fmt(appPrice)}</span>
                  </div>
                )}
                {vpcPrice > 0 && (
                  <div className="flex items-center justify-between text-sm">
                    <span style={{ color: 'hsl(var(--muted-fg))' }}>VPC (AWS network isolation)</span>
                    <span className="font-bold tabular-nums" style={{ color: NAVY }}>{sym}{fmt(vpcPrice)}</span>
                  </div>
                )}
                <div className="flex items-center justify-between pt-2 mt-1" style={{ borderTop: '1px solid #99f6e4' }}>
                  <span className="text-sm font-black" style={{ color: NAVY }}>Monthly Total (ex-tax)</span>
                  {allPriced
                    ? <span className="text-base font-black tabular-nums" style={{ color: NAVY }}>{sym}{fmt(total)}</span>
                    : <span className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>Custom quoted</span>
                  }
                </div>
                <p className="text-xs pt-1" style={{ color: 'hsl(var(--muted-fg))', borderTop: '1px solid #f1f5f9', paddingTop: '6px' }}>{c.cusExampleNote}</p>
              </div>
            </div>
          );
        })()}
      </Section>

      {/* Section 8: How pricing works */}
      <Section id="howpricing" title={c.s7Title}>
        <p className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>{c.s7Intro}</p>
        <div className="space-y-3">
          {[['1',c.step1Title,c.step1Desc],['2',c.step2Title,c.step2Desc],['3',c.step3Title,c.step3Desc],['4',c.step4Title,c.step4Desc]].map(([n,t,b]) => (
            <div key={n} className="flex items-start gap-3 rounded-xl border p-4">
              <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-black flex-shrink-0 text-white" style={{ background: NAVY }}>{n}</div>
              <div><p className="text-sm font-bold mb-0.5" style={{ color: NAVY }}>{t}</p><p className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>{b}</p></div>
            </div>
          ))}
        </div>

        {/* Worked Examples */}
        {(() => {
          const gp416 = appServers.find(s => s.code?.includes('GP') && String(s.code).includes('4') && String(s.code).includes('16')) || appServers[1];
          const egApp = gp416?.region_price_matrix?.[selectedRegion]?.[selectedCurrency] || 0;
          const vpcExtra = extras.find(e => e.name?.toUpperCase().includes('VPC'));
          const egVpc = vpcExtra?.prices_by_currency?.[selectedCurrency] || 0;
          const hana256 = hana.find(s => s.memory_gb === 256 && !s.is_base_for_hana);
          const hanaIsStatic = hana256?.pricing_mode === 'static';
          // Get HANA 256 price based on pricing mode
          const egHanaT1 = hanaIsStatic
            ? (hana256?.prices_by_currency?.[selectedCurrency] || 0)
            : (hana256?.price_matrix?.['1']?.[selectedCurrency] || 0);
          const egHanaT2 = hanaIsStatic
            ? egHanaT1   // same price at every tier - single price doesn't change
            : (hana256?.price_matrix?.['2']?.[selectedCurrency] || 0);
          // Label for the HANA upgrade line
          const hanaLineLabel = hanaIsStatic ? `HANA 256GB upgrade (fixed price)` : c.ex2L2;
          // Find the correct tier for a given user count (works with any number of tiers)
          const tierForUsers = (count) =>
            tiers.find(t => t.min_users <= count && (t.max_users == null || t.max_users >= count))
            || tiers[tiers.length - 1];

          const ex2Tier = tierForUsers(35);   // tier containing 35 users
          const ex3Tier = tierForUsers(60);   // tier containing 60 users

          const t1Pupm = ex2Tier?.prices_by_currency?.[selectedCurrency] || 0;
          const t1Min  = ex2Tier?.min_users || 30;
          const t1Max  = ex2Tier?.max_users || 39;
          const ex2TierName = ex2Tier?.tier_name || `${t1Min}–${t1Max} users`;

          // Next tier above 35 users (for the note)
          const ex2TierIdx = tiers.indexOf(ex2Tier);
          const noteTier   = ex2TierIdx >= 0 && ex2TierIdx < tiers.length - 1 ? tiers[ex2TierIdx + 1] : null;
          const t2PupmNote = noteTier?.prices_by_currency?.[selectedCurrency] || 0;

          // Example 3: correct tier for 60 users
          const t2PupmVal  = ex3Tier?.prices_by_currency?.[selectedCurrency] || 0;
          const ex3TierName = ex3Tier?.tier_name || `${ex3Tier?.min_users}–${ex3Tier?.max_users || ''} users`;

          const egStdTotal = 12 * stdPUPM + egApp + egVpc;
          const egAdvTotal = 35 * t1Pupm + egHanaT1 + egApp;
          const egMtpTotal = 60 * t2PupmVal + 2 * egApp;

          const ExampleCard = ({ badge, badgeStyle, title, term, lines, incl, total, note }) => (
            <div className="rounded-xl border bg-white overflow-hidden">
              <div className="px-4 py-2.5 flex items-center justify-between" style={{ background: 'hsl(var(--muted))', borderBottom: '1px solid hsl(var(--border))' }}>
                <div>
                  <p className="text-sm font-black" style={{ color: NAVY }}>{title}</p>
                  <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>{term} &middot; {selectedCurrency} &middot; {selectedRegionName}</p>
                </div>
                <span className="text-xs font-bold px-2.5 py-1 rounded-full" style={badgeStyle}>{badge}</span>
              </div>
              <div className="px-4 py-3 space-y-1.5">
                {lines.map(([label, val], i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <span style={{ color: 'hsl(var(--muted-fg))' }}>{label}</span>
                    <span className="font-bold tabular-nums" style={{ color: NAVY }}>{sym}{fmt(val)}</span>
                  </div>
                ))}
                {incl && incl.map((line, i) => (
                  <div key={`i${i}`} className="text-xs" style={{ color: '#15803d', fontWeight: 600 }}>{'\u2713'} {line}</div>
                ))}
                <div className="flex items-center justify-between pt-2 mt-1" style={{ borderTop: '1px solid hsl(var(--border))' }}>
                <span className="text-sm font-black" style={{ color: NAVY }}>{c.exTotal}</span>
                  <span className="text-base font-black tabular-nums" style={{ color: NAVY }}>{sym}{fmt(total)}</span>
                </div>
                {note && <p className="text-xs pt-1" style={{ color: 'hsl(var(--muted-fg))', borderTop: '1px solid hsl(var(--border))', paddingTop: '6px' }}>{note}</p>}
              </div>
            </div>
          );

          return (
            <div className="space-y-3">
              <p className="text-xs font-bold uppercase tracking-wide" style={{ color: NAVY }}>{c.workedExamplesLabel} - {selectedRegionName} · {selectedCurrency}</p>
              <ExampleCard
                badge="Standard" badgeStyle={{ background: 'hsl(var(--muted))', color: NAVY }}
                title={c.ex1Title} term={c.ex1Term}
                lines={[
                  [`12 users × ${sym}${fmt(stdPUPM)}/user (${c.ex1L1Suffix})`, 12 * stdPUPM],
                  ...(egApp > 0 ? [[c.ex1L2, egApp]] : []),
                  ...(egVpc > 0 ? [[c.ex1L3, egVpc]] : []),
                ]}
                total={egStdTotal}
              />
              <ExampleCard
                badge="Advanced" badgeStyle={{ background: NAVY, color: 'white' }}
                title={`Example 2 - C4P Advanced, 35 users (${ex2TierName}), HANA 256GB upgrade, 1 extra App Server`}
                term={c.ex2Term}
                lines={[
                  [`35 users × ${sym}${fmt(t1Pupm)}/user (${ex2TierName})`, 35 * t1Pupm],
                  ...(egHanaT1 > 0 ? [[hanaLineLabel, egHanaT1]] : []),
                  ...(egApp > 0 ? [[c.ex2L3, egApp]] : []),
                ]}
                incl={[c.ex2I1, c.ex2I2, c.ex2I3]}
                total={egAdvTotal}
                note={egHanaT1 > 0 && t2PupmNote > 0 && noteTier
                  ? hanaIsStatic
                    ? `At ${noteTier.min_users}+ users (${noteTier.tier_name}): user rate drops to ${sym}${fmt(t2PupmNote)}/user - HANA upgrade price is fixed at ${sym}${fmt(egHanaT1)}/mo.`
                    : `${c.exTierNote} (${sym}${fmt(t2PupmNote)}/user, HANA → ${sym}${fmt(egHanaT2)}/mo)`
                  : null
                }
              />
              <ExampleCard
                badge="MTPro" badgeStyle={{ background: 'hsl(265 89% 45%)', color: 'white' }}
                title={`Example 3 - C4P MTPro, 60 users (${ex3TierName}), 2 extra App Servers`}
                term={c.ex3Term}
                lines={[
                  [`60 users × ${sym}${fmt(t2PupmVal)}/user (${ex3TierName})`, 60 * t2PupmVal],
                  ...(egApp > 0 ? [[c.ex3L2, egApp],[c.ex3L3, egApp]] : []),
                ]}
                incl={[c.ex3I1, c.ex3I2]}
                total={egMtpTotal}
              />
            </div>
          );
        })()}
      </Section>

      {/* Section 7: FAQs */}
      <Section id="faq" title={c.s8Title}>
        <div className="space-y-3">
          {[1,2,3,4,5,6,7,8,9].map(i => (
            <div key={i} className="rounded-xl border bg-white p-4">
              <p className="text-sm font-bold mb-1.5" style={{ color: NAVY }}>Q: {c[`faq${i}Q`]}</p>
              <p className="text-sm leading-relaxed" style={{ color: 'hsl(var(--muted-fg))' }}>{c[`faq${i}A`]}</p>
            </div>
          ))}
        </div>
      </Section>

      <Separator />
      <p className="text-xs text-center" style={{ color: 'hsl(var(--muted-fg))' }}>{c.footerNote}</p>
    </div>
  );
}
